import React, { useState, useEffect, useMemo } from 'react';
import { 
  Modal, 
  Row, 
  Col, 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Badge,
  Table,
  Card
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { 
  FaUserShield, 
  FaCheck, 
  FaTimes, 
  FaUsers, 
  FaShieldAlt,
  FaSave,
  FaUndo,
  FaInfoCircle
} from 'react-icons/fa';
import { 
  BsX, 
  BsCheck2All, 
  BsInfoCircle 
} from 'react-icons/bs';

import rbacService from '../../../services/rbacService';

/**
 * RoleAssignmentModal
 *
 * This modal handles role assignment for users following RBAC principles.
 * Users can only receive permissions through role assignments.
 *
 * State Shape:
 * - selectedRoleIds: Set<string> - Currently selected role IDs
 * - originalRoleIds: Set<string> - Original role IDs from API
 * - availableRoles: Array<Object> - All roles that can be assigned
 *
 * Toggle Logic:
 * 1. Check if roleId exists in selectedRoleIds Set
 * 2. If exists: delete from Set (deselect)
 * 3. If not exists: add to Set (select)
 * 4. Always create new Set instance for React updates
 *
 * Props:
 *  - show (boolean): Whether the modal is visible
 *  - user (object): User object containing id, name, email, etc.
 *  - onHide () => void: Callback to close the modal
 *  - onSaved (Array<string>) => void: Callback after successful role assignment with updated role IDs
 *  - refreshTrigger (number): Increment this to force refresh of role data
 */
const RoleAssignmentModal = ({ show, user, onHide, onSaved, refreshTrigger = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState(new Set());
  const [originalRoleIds, setOriginalRoleIds] = useState(new Set());

  // Helper function to get consistent role ID
  const getRoleId = (role) => {
    // Handle different role object structures from API
    const id = role.id || role.role_id || role._id || role.roleId;
    if (!id) {
      console.warn('Role object missing ID:', role);
      return String(role.name || 'unknown');
    }
    return String(id);
  };

  // Enhanced change detection using useMemo for performance
  const hasChanges = useMemo(() => {
    if (selectedRoleIds.size !== originalRoleIds.size) return true;
    for (const id of selectedRoleIds) {
      if (!originalRoleIds.has(id)) return true;
    }
    return false;
  }, [selectedRoleIds, originalRoleIds]);

  // Load available roles and current user roles
  useEffect(() => {
    if (!show || !user?.id) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all available roles
        const rolesResponse = await rbacService.getRoles({
          page_size: 100
        });
        console.log('Roles response:', rolesResponse);

        let roles = [];
        if (rolesResponse.success && rolesResponse.data) {
          if (Array.isArray(rolesResponse.data)) {
            roles = rolesResponse.data;
          } else if (rolesResponse.data.items && Array.isArray(rolesResponse.data.items)) {
            roles = rolesResponse.data.items;
          }
        }

        console.log('Available roles loaded:', roles);
        setAvailableRoles(roles);

        // Fetch current user roles with cache busting
        // The cache is automatically cleared by assignRoleToUser/removeRoleFromUser
        // We use bustCache: true to force a fresh fetch
        const userRolesResponse = await rbacService.getUserRoles(user.id, {
          bustCache: true // Force fresh data, bypassing cache
        });
        console.log('User roles response:', userRolesResponse);

        // Handle different response formats from the API
        let userRoles = [];
        if (userRolesResponse.success && userRolesResponse.data) {
          if (Array.isArray(userRolesResponse.data)) {
            userRoles = userRolesResponse.data;
          } else if (userRolesResponse.data.roles && Array.isArray(userRolesResponse.data.roles)) {
            userRoles = userRolesResponse.data.roles;
          }
        }

        // Initialize sets properly with consistent role IDs
        const roleIds = new Set(userRoles.map(role => getRoleId(role)));
        console.log('Initialized role IDs:', Array.from(roleIds));
        console.log('User roles structure:', userRoles);

        setSelectedRoleIds(roleIds);
        setOriginalRoleIds(new Set(roleIds));

      } catch (error) {
        console.error('Failed to load roles:', error);
        toast.error('Failed to load roles');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [show, user?.id, refreshTrigger]); // Added refreshTrigger to force refresh

  // Handle role toggle with Set-based state management
  const toggle = (roleId) => {
    if (!roleId) {
      console.warn('Attempted to toggle undefined roleId');
      return;
    }
    
    console.log('Toggling role:', roleId);
    setSelectedRoleIds(prev => {
      const next = new Set(prev);
      const wasSelected = next.has(roleId);
      
      if (wasSelected) {
        next.delete(roleId);
        console.log('Removed role:', roleId);
      } else {
        next.add(roleId);
        console.log('Added role:', roleId);
      }
      
      console.log('New selected roles:', Array.from(next));
      return next;
    });
  };

  // Handle save with proper API integration and UI state updates
  const handleSave = async () => {
    if (!hasChanges) return;

    setSaving(true);
    try {
      // Calculate roles to add and remove
      const currentRoleIds = Array.from(selectedRoleIds);
      const originalRoleIdsArray = Array.from(originalRoleIds);
      
      const rolesToAdd = currentRoleIds.filter(id => !originalRoleIds.has(id));
      const rolesToRemove = originalRoleIdsArray.filter(id => !selectedRoleIds.has(id));
      
      console.log('Role changes:', { 
        userId: user.id, 
        rolesToAdd, 
        rolesToRemove 
      });

      // Remove roles first
      for (const roleId of rolesToRemove) {
        await rbacService.removeRoleFromUser(user.id, roleId);
      }

      // Add new roles
      for (const roleId of rolesToAdd) {
        await rbacService.assignRoleToUser(user.id, roleId);
      }

      // Update UI state immediately to reflect changes
      setOriginalRoleIds(new Set(selectedRoleIds));

      // Success feedback
      const userName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
      toast.success(
        <div>
          <strong>Success!</strong><br/>
          Roles updated for {userName}
        </div>
      );

      // Map role IDs to role names for display in the parent component
      const updatedRoleNames = availableRoles
        .filter(role => currentRoleIds.includes(getRoleId(role)))
        .map(role => role.name || role.display_name);

      // Notify parent component with updated role names and close modal
      if (onSaved) {
        onSaved(updatedRoleNames);
      }
      onHide();
      
    } catch (error) {
      console.error('Role assignment error:', error);
      toast.error(
        <div>
          <strong>Error!</strong><br/>
          Failed to update roles: {error.message || 'Unknown error'}
        </div>
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setSelectedRoleIds(new Set(originalRoleIds));
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} dialogClassName="modal-lg">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <FaUserShield className="text-primary" />
          Assign Roles - {user?.full_name || user?.first_name + ' ' + user?.last_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Loading roles...</p>
            </div>
          </div>
        ) : (
          <>
            {/* User Information */}
            <Card className="mb-4">
              <Card.Body className="p-3">
                <Row>
                  <Col md={8}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                        <FaUsers size={20} className="text-white" />
                      </div>
                      <div>
                        <h6 className="mb-0">{user?.full_name || user?.first_name + ' ' + user?.last_name}</h6>
                        <small className="text-muted">{user?.email}</small>
                      </div>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-end">
                      <Badge bg={selectedRoleIds.size > 0 ? 'success' : 'secondary'}>
                        {selectedRoleIds.size} Role{selectedRoleIds.size !== 1 ? 's' : ''} Assigned
                      </Badge>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Change Detection Alert */}
            {hasChanges && (
              <Alert variant="warning" className="d-flex align-items-center mb-4">
                <FaInfoCircle className="me-2" />
                <div>
                  <strong>Unsaved Changes</strong>
                  <br />
                  <small>You have made changes to the role assignments. Don't forget to save!</small>
                </div>
              </Alert>
            )}

            {/* Available Roles */}
            <div className="mb-4">
              <h6 className="mb-3">
                <FaShieldAlt className="me-2" />
                Available Roles
              </h6>
              
              {availableRoles.length === 0 ? (
                <Alert variant="info" className="text-center">
                  <BsInfoCircle className="me-2" />
                  No roles available for assignment
                </Alert>
              ) : (
                <Table hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th>Role Name</th>
                      <th>Description</th>
                      <th className="text-center">Assigned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableRoles.map((role) => {
                      const roleId = getRoleId(role);
                      const isSelected = selectedRoleIds.has(roleId);
                      return (
                        <tr key={roleId} className={isSelected ? 'table-success' : ''}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <FaShieldAlt size={16} className={isSelected ? 'text-success' : 'text-muted'} />
                              <strong>{role.name || role.display_name}</strong>
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">{role.description || 'No description available'}</small>
                          </td>
                          <td className="text-center">
                            <Form.Check 
                              type="switch" 
                              id={`role-switch-${roleId}`}
                              checked={isSelected}
                              onChange={() => toggle(roleId)}
                              disabled={saving}
                              size="lg"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>

            {/* Current Assignments Summary */}
            {selectedRoleIds.size > 0 && (
              <Card>
                <Card.Header>
                  <h6 className="mb-0">
                    <BsCheck2All className="me-2" />
                    Selected Roles ({selectedRoleIds.size})
                  </h6>
                </Card.Header>
                <Card.Body className="p-3">
                  <div className="d-flex flex-wrap gap-2">
                    {availableRoles
                      .filter(role => selectedRoleIds.has(getRoleId(role)))
                      .map((role) => {
                        const roleId = getRoleId(role);
                        return (
                          <Badge 
                            key={roleId} 
                            bg="success" 
                            className="p-2 d-flex align-items-center gap-1"
                          >
                            <FaShieldAlt size={12} />
                            {role.name || role.display_name}
                            <button 
                              className="btn btn-sm btn-link text-white p-0 ms-1"
                              onClick={() => toggle(roleId)}
                              disabled={saving}
                              style={{ fontSize: '12px' }}
                              aria-label={`Remove ${role.name || role.display_name} role`}
                            >
                              <FaTimes />
                            </button>
                          </Badge>
                        );
                      })
                    }
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between align-items-center w-100">
          <div>
            {hasChanges && (
              <Alert variant="warning" className="p-2 mb-0 d-flex align-items-center">
                <FaInfoCircle className="me-2" />
                <small>You have unsaved changes</small>
              </Alert>
            )}
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
              <BsX className="me-1" />
              Cancel
            </Button>
            {hasChanges && (
              <Button 
                variant="outline-warning" 
                onClick={handleReset}
                disabled={saving}
              >
                <FaUndo className="me-1" />
                Reset
              </Button>
            )}
            <Button 
              variant="primary" 
              onClick={handleSave} 
              disabled={saving || !hasChanges}
              className="d-flex align-items-center gap-2"
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default RoleAssignmentModal;